import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchOrderHubOrderLines } from '@/api/order-hub';
import { buildOrderLinesQs } from '../-utils/listParams';
import { ordersHubKeys } from './useOrdersHubList';

/** Shared query for a single order's lines. Caches the raw `{ data, meta }`
 *  envelope under `ordersHubKeys.lines(orderId)` — the SAME slot the list's
 *  per-order fan-out (`useOrdersHubLines`) uses — so a line write refreshes both
 *  surfaces. Both consumers MUST store the identical (envelope) shape here: the
 *  fan-out needs `meta.totalCount`, and mixing an unwrapped-array shape into the
 *  same slot makes whichever surface reads a cache hit it didn't populate crash
 *  (e.g. `lines.map` on an envelope object). Detail consumers unwrap via
 *  `select` below; they never rely on the queryFn's `.then` running, since a
 *  fresh cache hit skips the queryFn entirely. */
export function orderHubOrderLinesQueryOptions(orderId: string) {
  return queryOptions({
    queryKey: ordersHubKeys.lines(orderId),
    queryFn: () => fetchOrderHubOrderLines(buildOrderLinesQs(orderId)),
    staleTime: 5 * 60_000,
  });
}

/** Unwrapped lines array for the detail/overview surfaces. Selects `.data` from
 *  the shared envelope cache so it stays shape-compatible with the fan-out. */
export function useOrderHubOrderLinesList(orderId: string) {
  return useQuery({
    ...orderHubOrderLinesQueryOptions(orderId),
    select: res => res.data,
  });
}
